/**
 * SharePreferenceCardPage - Share preference card with off-platform providers
 * 
 * This page displays a user's preference card to external providers who
 * don't have Phygital accounts. ALL CONTENT IS CONFIGURABLE from the backend.
 * 
 * URL: /share/card/:share_id
 * 
 * Backend Settings (per category):
 * - page_title, page_subtitle
 * - cta_button_text, cta_button_link
 * - provider_onboarding_prompt
 * - theme colors
 * - which sections to display
 * 
 * Last Updated: December 2025
 */

import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Building2,
  Calendar,
  Clock,
  Utensils,
  Accessibility,
  Heart,
  Music,
  Sparkles,
  Star,
  Copy,
  Check,
  ExternalLink,
  ArrowUp,
  Download,
  QrCode,
  ChefHat,
  Scissors,
  UserPlus,
  Users,
  Eye,
  X
} from "lucide-react";

// Category-specific default configurations
const CATEGORY_CONFIGS = {
  hair: {
    page_title: "Client Preferences",
    page_subtitle: "Shared with you for a personalized experience",
    cta_button_text: "Join Phygital to Manage Clients",
    provider_prompt: "Want to manage client preferences digitally? Join Phygital for Beauty Pros.",
    icon: Scissors,
  },
  makeup: {
    page_title: "Client Preferences",
    page_subtitle: "Shared with you for a personalized experience",
    cta_button_text: "Join Phygital for Beauty Pros",
    provider_prompt: "Streamline your client intake with Phygital.",
    icon: Sparkles,
  },
  culinary: {
    page_title: "Guest Preferences",
    page_subtitle: "Dietary needs and preferences for your event",
    cta_button_text: "Join Phygital for Caterers",
    provider_prompt: "Get detailed dietary preferences from event attendees.",
    icon: ChefHat,
  },
  events: {
    page_title: "Attendee Preferences",
    page_subtitle: "Shared for event planning",
    cta_button_text: "Host Your Event with Phygital",
    provider_prompt: "Collect attendee preferences for your next event.",
    icon: Calendar,
  },
  wellness: {
    page_title: "Client Wellness Profile",
    page_subtitle: "Health preferences and needs",
    cta_button_text: "Join Phygital for Wellness",
    provider_prompt: "Manage client wellness profiles digitally.",
    icon: Heart,
  },
  nails: {
    page_title: "Client Preferences",
    page_subtitle: "Nail preferences shared with you",
    cta_button_text: "Join Phygital for Nail Techs",
    provider_prompt: "Capture client nail preferences digitally.",
    icon: Sparkles,
  },
  lashes: {
    page_title: "Client Preferences",
    page_subtitle: "Lash preferences shared with you",
    cta_button_text: "Join Phygital for Lash Techs",
    provider_prompt: "Know your client's lash preferences before every set.",
    icon: Eye,
  },
  instructor: {
    page_title: "Participant Profile",
    page_subtitle: "Learning preferences shared with you",
    cta_button_text: "Join Phygital for Instructors",
    provider_prompt: "Collect participant skill levels and learning goals.",
    icon: User,
  },
  default: {
    page_title: "Preferences Shared With You",
    page_subtitle: "View and save these preferences",
    cta_button_text: "Learn More About Phygital",
    provider_prompt: "Phygital helps you collect and manage client preferences.",
    icon: User,
  }
};

// Get image URL helper
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  return `${API}${url.startsWith('/') ? '' : '/'}${url}`;
};

export default function SharePreferenceCardPage() {
  const { share_id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareData, setShareData] = useState(null);
  const [displaySettings, setDisplaySettings] = useState(null);
  const [copied, setCopied] = useState(false);
  const [lightboxImg, setLightboxImg] = useState(null);
  // Iter 9.52 — eye-icon "full preference card snapshot" popup with dark overlay.
  // Screen 15 of the Phygital flow. Opens the full preference card as a large
  // preview over a bg-black/80 overlay (mirroring the mockup where the host
  // clicks the eye to see everything the planner chose).
  const [showCardPreview, setShowCardPreview] = useState(false);

  useEffect(() => {
    if (share_id) {
      fetchShareData();
    }
  }, [share_id]);

  const fetchShareData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/share/preference-card/${share_id}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Share not found");
      }
      const data = await res.json();
      setShareData(data);
      setDisplaySettings(data.display_settings || {});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  // Get category-specific config
  const category = shareData?.category || 'default';
  const config = CATEGORY_CONFIGS[category] || CATEGORY_CONFIGS.default;
  
  // Dynamic color from admin preset (fallback to sensible defaults)
  const CATEGORY_FALLBACK_COLORS = {
    hair: "#e83e8c", makeup: "#9c57f5", nails: "#f15a70", lashes: "#774cda",
    events: "#6366F1", event: "#6366F1", instructor: "#4a90d9", culinary: "#f28c28",
    wellness: "#75d6d6", travel: "#73b7ff", dispensary: "#60d680", retail: "#9950fa",
    default: "#6366F1"
  };
  const presetColor = shareData?.category_preset?.color || CATEGORY_FALLBACK_COLORS[category] || CATEGORY_FALLBACK_COLORS.default;
  
  // Override with backend settings if available
  const shareCopy = shareData?.share_copy || {};
  const pageTitle = displaySettings?.share_page_title || shareCopy.headline || config.page_title;
  const pageSubtitle = displaySettings?.share_page_subtitle || shareCopy.subtext || config.page_subtitle;
  const ctaText = displaySettings?.share_cta_text || shareCopy.cta_text || config.cta_button_text;
  const ctaLink = displaySettings?.share_cta_link || shareCopy.demo_link || `/${category === 'default' ? '' : category}`;
  const providerPrompt = displaySettings?.provider_onboarding_prompt || shareCopy.provider_cta || config.provider_prompt;
  const IconComponent = config.icon;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-md mx-auto space-y-4">
          <Skeleton className="h-12 w-3/4" />
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-[400px] w-full rounded-2xl" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-red-100 mx-auto mb-4 flex items-center justify-center">
            <User className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Share Not Found</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <p className="text-sm text-gray-500">This share link may have expired or been revoked.</p>
          <Button asChild className="mt-4">
            <Link to="/">Go to Phygital</Link>
          </Button>
        </div>
      </div>
    );
  }

  const person = shareData?.person || {};
  const rawPreferences = shareData?.preferences || {};
  // Preferences may be nested under "defaults" key from user_defaults collection
  const preferences = rawPreferences?.defaults || rawPreferences || {};
  const event = shareData?.event || {};
  const categoryPreset = shareData?.category_preset || {};
  const userInspo = shareData?.inspo || [];
  const defaultsToCollect = categoryPreset.defaults_to_collect || [];

  const displayName = person.name || `${person.first_name || ''} ${person.last_name || ''}`.trim() || 'Guest';
  const initials = displayName.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() || "?";

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: presetColor }}>
              <IconComponent className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold text-gray-900">Phygital</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => copyToClipboard(window.location.href)}>
            {copied ? <Check className="w-4 h-4" /> : <ArrowUp className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6 space-y-6">
        {/* Page Header - All text from backend */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900" data-testid="share-page-title">
            {pageTitle}
          </h1>
          <p className="text-gray-600 mt-1" data-testid="share-page-subtitle">
            {pageSubtitle}
          </p>
        </div>

        {/* Preference Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden" data-testid="share-preference-card">
          {/* Card Header with Background */}
          <div className="relative">
            {(event.card_background || event.cover_image) ? (
              <>
                <div className="absolute inset-0">
                  <img 
                    src={getImageUrl(event.card_background || event.cover_image)} 
                    alt={event.name || 'Event'} 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-black/70" />
                </div>
              </>
            ) : (
              <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, ${presetColor}, ${presetColor}dd)` }} />
            )}
            
            <div className="relative px-6 py-6 text-white">
              {/* Dynamic Card Label */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium tracking-wider opacity-80" data-testid="card-label">
                  {displaySettings?.card_label || shareData?.card_label || "PREFERENCE CARD"}
                </span>
                <button
                  type="button"
                  onClick={() => setShowCardPreview(true)}
                  data-testid="open-card-preview"
                  aria-label="Preview full preference card"
                  title="Preview full preference card"
                  className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 border border-white/30 flex items-center justify-center transition"
                >
                  <Eye className="w-4 h-4 text-white" />
                </button>
              </div>
              
              {/* Event/Context Title */}
              {event.name && (
                <div className="flex items-center gap-2 mb-4">
                  {event.logo_image && (
                    <div className="w-10 h-10 rounded-lg border-2 border-white/50 overflow-hidden bg-black/20">
                      <img 
                        src={getImageUrl(event.logo_image)} 
                        alt={event.name} 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <h3 className="font-semibold text-lg">{event.name}</h3>
                </div>
              )}
              
              {/* Profile Section */}
              <div className="flex flex-col items-center">
                <div className="w-24 h-24 rounded-full bg-white/20 overflow-hidden border-4 border-white/50 shadow-lg mb-3">
                  {(person.profile_photo_url || person.profile_image || person.picture) ? (
                    <img 
                      src={getImageUrl(person.profile_photo_url || person.profile_image || person.picture)} 
                      alt={displayName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-3xl font-bold bg-white/10">
                      {initials}
                    </div>
                  )}
                </div>
                
                <h2 className="text-xl font-bold drop-shadow-md text-center">{displayName}</h2>
                {(person.title || person.company) && (
                  <p className="text-sm opacity-80 text-center">
                    {person.title}{person.title && person.company ? ' at ' : ''}{person.company}
                  </p>
                )}
                
                {/* Labels */}
                {shareData?.labels && shareData.labels.length > 0 && (
                  <div className="flex flex-wrap justify-center gap-1.5 mt-3">
                    {shareData.labels.map((lbl, idx) => (
                      <Badge key={idx} className="text-xs bg-white/20 text-white border-0">
                        {lbl}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Dynamic Preference Sections - ONLY blocks the host asks for + client chose to share */}
          <div className="p-6 space-y-4">
            {/* Experience Context */}
            {(shareData?.experience_name || shareData?.experience_date) && (
              <div className="bg-gray-50 rounded-xl px-4 py-3 border border-gray-100" data-testid="share-experience-context">
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">Service</p>
                <p className="font-semibold text-gray-900">{shareData.experience_name}</p>
                {shareData.experience_date && (
                  <p className="text-sm text-gray-500 mt-0.5">{new Date(shareData.experience_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</p>
                )}
                {shareData.provider?.name && (
                  <p className="text-sm text-gray-500 mt-0.5">Provider: {shareData.provider.name}</p>
                )}
              </div>
            )}

            {defaultsToCollect.map(blockKey => {
              const blockData = preferences[blockKey];
              if (!blockData || (typeof blockData === 'object' && Object.keys(blockData).length === 0)) return null;
              
              // Render block based on its type
              const renderBlock = () => {
                if (typeof blockData === 'string') {
                  return <p className="text-gray-600 italic">"{blockData}"</p>;
                }
                if (Array.isArray(blockData)) {
                  return (
                    <div className="flex flex-wrap gap-2">
                      {blockData.map((item, idx) => (
                        <Badge key={idx} variant="outline" className="text-gray-600">{typeof item === 'string' ? item : JSON.stringify(item)}</Badge>
                      ))}
                    </div>
                  );
                }
                if (typeof blockData === 'object') {
                  return (
                    <div className="space-y-2">
                      {Object.entries(blockData).map(([fieldKey, fieldValue]) => {
                        if (!fieldValue || fieldKey === 'filled' || (Array.isArray(fieldValue) && fieldValue.length === 0)) return null;
                        const label = fieldKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                        return (
                          <div key={fieldKey}>
                            <span className="text-xs font-medium text-gray-500">{label}</span>
                            <div className="mt-0.5">
                              {Array.isArray(fieldValue) ? (
                                <div className="flex flex-wrap gap-1.5">
                                  {fieldValue.map((v, i) => (
                                    <Badge key={i} className={blockKey === 'dietary' && fieldKey === 'allergies' ? 'bg-red-100 text-red-700 border-0' : 'bg-gray-100 text-gray-700 border-0'}>
                                      {typeof v === 'string' ? v : JSON.stringify(v)}
                                    </Badge>
                                  ))}
                                </div>
                              ) : typeof fieldValue === 'boolean' ? (
                                <Badge className="bg-gray-100 text-gray-700 border-0">{fieldValue ? 'Yes' : 'No'}</Badge>
                              ) : (
                                <p className="text-sm text-gray-700">{String(fieldValue)}</p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                }
                return null;
              };
              
              const sectionLabel = blockKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
              const SECTION_ICONS = {
                dietary: Utensils, accessibility: Accessibility, interests: Heart,
                hair: Scissors, face: Sparkles, lash: Sparkles, strip_lash: Eye, nails: Sparkles,
                wellness: Heart, style: Star, travel: MapPin, builder: UserPlus,
                audience: Users
              };
              const SectionIcon = SECTION_ICONS[blockKey] || Star;
              
              return (
                <div key={blockKey} data-testid={`share-section-${blockKey}`}>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2">
                    <SectionIcon className="w-4 h-4" />
                    {displaySettings?.[`${blockKey}_section_title`] || sectionLabel}
                  </h4>
                  {renderBlock()}
                </div>
              );
            })}

            {/* Fallback: render any preference blocks not covered by defaultsToCollect */}
            {Object.entries(preferences).filter(([key]) => !defaultsToCollect.includes(key) && key !== 'filled' && key !== 'updated_at').map(([blockKey, blockData]) => {
              if (!blockData || (typeof blockData === 'object' && !Array.isArray(blockData) && Object.keys(blockData).filter(k => k !== 'filled' && k !== 'updated_at').length === 0)) return null;
              const sectionLabel = blockKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
              return (
                <div key={blockKey} data-testid={`share-section-${blockKey}`}>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2">
                    <Star className="w-4 h-4" />
                    {sectionLabel}
                  </h4>
                  {typeof blockData === 'object' && !Array.isArray(blockData) ? (
                    <div className="space-y-2">
                      {Object.entries(blockData).filter(([k]) => k !== 'filled' && k !== 'updated_at').map(([fk, fv]) => {
                        if (!fv || (Array.isArray(fv) && fv.length === 0)) return null;
                        return (
                          <div key={fk}>
                            <span className="text-xs font-medium text-gray-500">{fk.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                            <div className="mt-0.5">
                              {Array.isArray(fv) ? (
                                <div className="flex flex-wrap gap-1.5">{fv.map((v, i) => <Badge key={i} className="bg-gray-100 text-gray-700 border-0">{String(v)}</Badge>)}</div>
                              ) : <p className="text-sm text-gray-700">{String(fv)}</p>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : Array.isArray(blockData) ? (
                    <div className="flex flex-wrap gap-1.5">{blockData.map((v, i) => <Badge key={i} variant="outline" className="text-gray-600">{String(v)}</Badge>)}</div>
                  ) : <p className="text-sm text-gray-600">{String(blockData)}</p>}
                </div>
              );
            })}
            
            
            {/* Inspiration Images - only if inspo enabled for this category */}
            {userInspo.length > 0 && (
              <div data-testid="share-inspo-section">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2">
                  <Star className="w-4 h-4" />
                  Inspiration ({userInspo.length})
                </h4>
                <div className="grid grid-cols-3 gap-2.5">
                  {userInspo.slice(0, 9).map((img, idx) => {
                    const imgUrl = img.image_url || img.url;
                    return (
                      <div key={idx} className="aspect-square rounded-xl overflow-hidden bg-gray-100 cursor-pointer hover:opacity-80 hover:ring-2 hover:ring-purple-300 transition-all shadow-sm" onClick={() => setLightboxImg(getImageUrl(imgUrl))}>
                        {imgUrl && <img src={getImageUrl(imgUrl)} alt={img.title || ''} className="w-full h-full object-cover" />}
                      </div>
                    );
                  })}
                </div>
                {userInspo.length > 9 && (
                  <p className="text-xs text-gray-400 text-center mt-2">+{userInspo.length - 9} more inspiration images</p>
                )}
              </div>
            )}
            
            {/* Contact Info (if allowed) */}
            {displaySettings?.show_contact_on_share && person.email && (
              <div className="pt-3 border-t">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Contact
                </h4>
                <p className="text-gray-600">{person.email}</p>
              </div>
            )}
          </div>
        </div>

        {/* Linked Experiences (for personal events with multiple service types) */}
        {shareData?.linked_experiences && shareData.linked_experiences.length > 0 && (
          <div className="space-y-3" data-testid="linked-experiences-section">
            <h3 className="text-sm font-semibold text-gray-700 px-1">Linked Experiences</h3>
            {shareData.linked_experiences.map((exp, i) => {
              const expCatConfig = CATEGORY_CONFIGS[exp.category] || CATEGORY_CONFIGS.default;
              const ExpCardIcon = expCatConfig.icon;
              const expPrefs = exp.preferences || {};
              const expInspo = exp.inspo_images || [];
              return (
                <Card key={exp.experience_id || i} className="border-gray-100 overflow-hidden" data-testid={`linked-card-${exp.category || i}`}>
                  {/* Category color header */}
                  <div className="px-4 py-3 flex items-center gap-3" style={{ background: CATEGORY_FALLBACK_COLORS[exp.category] || presetColor }}>
                    <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                      <ExpCardIcon className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white">{exp.title || `${(exp.category || '').replace(/^\w/, c => c.toUpperCase())} Experience`}</p>
                      <p className="text-[11px] text-white/80">
                        {exp.provider_name || <span className="italic text-white/60">Provider: Still deciding</span>}
                        {exp.date && <span> &middot; {new Date(exp.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
                      </p>
                    </div>
                  </div>
                  <CardContent className="p-4 space-y-3">
                    {/* Inspo images */}
                    {expInspo.length > 0 && (
                      <div className="flex gap-2.5 overflow-x-auto pb-1">
                        {expInspo.map((img, idx) => (
                          <div key={idx} className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100 cursor-pointer hover:opacity-80 hover:ring-2 hover:ring-purple-300 transition-all shadow-sm" onClick={() => setLightboxImg(img)}>
                            <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Preference badges */}
                    {Object.keys(expPrefs).length > 0 && (
                      <div className="space-y-1.5">
                        {Object.entries(expPrefs).map(([key, val]) => {
                          if (!val) return null;
                          const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                          return (
                            <div key={key} className="flex items-baseline gap-2">
                              <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider w-16 flex-shrink-0">{label}</span>
                              {Array.isArray(val) ? (
                                <div className="flex flex-wrap gap-1">
                                  {val.map((v, vi) => <Badge key={vi} className="bg-gray-100 text-gray-700 border-0 text-[10px]">{v}</Badge>)}
                                </div>
                              ) : (
                                <span className="text-xs text-gray-700">{String(val)}</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {/* Notes */}
                    {exp.notes && <p className="text-xs text-gray-500 italic">{exp.notes}</p>}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Provider CTA - Subtle, trackable */}
        <div className="flex items-center justify-center gap-2 py-3 px-4 bg-gray-50 rounded-lg border border-gray-100">
          <span className="text-xs text-gray-400">{providerPrompt}</span>
          <Link 
            to={ctaLink} 
            data-testid="share-cta-btn"
            onClick={() => {
              // Track CTA click for analytics
              try {
                fetch(`${API}/analytics/track`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    event: 'share_page_provider_cta_click',
                    share_id: share_id,
                    category,
                    timestamp: new Date().toISOString()
                  })
                }).catch(() => {});
              } catch(e) {}
            }}
            className="text-xs font-medium hover:opacity-80 underline underline-offset-2 transition-colors"
            style={{ color: presetColor }}
          >
            {ctaText}
          </Link>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-gray-400 pb-6">
          <p>Shared via <Link to="/" className="hover:underline" style={{ color: presetColor }}>Phygital</Link></p>
        </div>
      </div>

      {/* Image Lightbox */}
      {lightboxImg && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setLightboxImg(null)}>
          <img src={lightboxImg} alt="" className="max-w-full max-h-[90vh] object-contain rounded-lg" />
        </div>
      )}

      {/* Preference Card Preview - dark-overlay popup (mockup screen 15) */}
      {showCardPreview && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4 sm:p-8"
          onClick={() => setShowCardPreview(false)}
          data-testid="card-preview-overlay"
        >
          <div
            className="relative w-full max-w-md max-h-[92vh] overflow-y-auto rounded-3xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="card-preview-popup"
          >
            <button
              type="button"
              onClick={() => setShowCardPreview(false)}
              aria-label="Close preview"
              data-testid="close-card-preview"
              className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/95 hover:bg-white shadow flex items-center justify-center"
            >
              <X className="w-4 h-4 text-stone-700" />
            </button>

            {/* Hero */}
            <div className="relative overflow-hidden rounded-t-3xl">
              {(person.header_image || event.cover_image) ? (
                <img src={getImageUrl(person.header_image || event.cover_image)} alt="" className="w-full h-40 object-cover" />
              ) : (
                <div className="w-full h-40" style={{ background: `linear-gradient(135deg, ${presetColor}, ${presetColor}dd)` }} />
              )}
              <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, transparent, ${presetColor}aa)` }} />
              <div className="absolute bottom-3 left-4 right-4 flex items-center gap-3">
                <div className="w-14 h-14 rounded-full bg-white/20 overflow-hidden border-2 border-white shadow">
                  {(person.profile_photo_url || person.profile_image || person.picture) ? (
                    <img src={getImageUrl(person.profile_photo_url || person.profile_image || person.picture)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-lg font-bold text-white bg-white/10">{initials}</div>
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-white text-lg font-bold truncate drop-shadow">{displayName}</p>
                  <p className="text-white/90 text-[11px] uppercase tracking-wider font-semibold">Preference Card Snapshot</p>
                </div>
              </div>
            </div>

            {/* Meta */}
            <div className="px-5 py-4 border-b border-stone-100 space-y-1">
              {(shareData?.service || shareData?.category) && (
                <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Service</p>
              )}
              <p className="text-sm font-semibold text-stone-900">
                {shareData?.service || (shareData?.category ? String(shareData.category).replace(/^\w/, (c) => c.toUpperCase()) : "Experience")}
              </p>
              {event.name && (
                <>
                  <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold mt-2">Occasion</p>
                  <p className="text-sm text-stone-900">{event.name}</p>
                </>
              )}
            </div>

            {/* Preferences snapshot */}
            <div className="px-5 py-4 space-y-3">
              <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Preferences</p>
              {(() => {
                const prefs = shareData?.preferences_snapshot || shareData?.defaults || {};
                const entries = Object.entries(prefs).filter(([k, v]) => k && v && !k.startsWith("_") && k !== "visibility");
                if (entries.length === 0) {
                  return <p className="text-xs text-stone-400 italic">No preferences shared for this experience.</p>;
                }
                return entries.map(([k, v]) => (
                  <div key={k} className="flex items-start gap-2 py-1 border-b border-stone-50 last:border-0">
                    <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold w-24 shrink-0 pt-0.5">{k.replace(/_/g, " ")}</p>
                    <div className="flex-1 flex flex-wrap gap-1">
                      {Array.isArray(v)
                        ? v.map((iv, i) => (
                            <span key={`${k}-${i}`} className="text-[11px] font-semibold text-stone-800 bg-stone-100 rounded-full px-2 py-0.5">
                              {typeof iv === "object" ? (iv.label || iv.name || JSON.stringify(iv)) : String(iv)}
                            </span>
                          ))
                        : (typeof v === "object"
                            ? <span className="text-[11px] text-stone-800">{JSON.stringify(v)}</span>
                            : <span className="text-[11px] font-semibold text-stone-800 bg-stone-100 rounded-full px-2 py-0.5">{String(v)}</span>)}
                    </div>
                  </div>
                ));
              })()}
            </div>

            {/* Inspiration preview */}
            {Array.isArray(shareData?.inspo) && shareData.inspo.length > 0 && (
              <div className="px-5 pb-5">
                <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold mb-2">Inspiration</p>
                <div className="grid grid-cols-4 gap-1.5">
                  {shareData.inspo.slice(0, 8).map((img, i) => (
                    <img key={i} src={getImageUrl(typeof img === "string" ? img : (img?.url || img?.image_url))} alt="" className="aspect-square object-cover rounded-lg border border-stone-200" />
                  ))}
                </div>
              </div>
            )}

            <div className="px-5 pb-5 pt-1">
              <button
                type="button"
                onClick={() => setShowCardPreview(false)}
                className="w-full rounded-full bg-stone-900 text-white text-sm font-bold py-2.5 hover:bg-black"
                data-testid="close-card-preview-cta"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
